import React, { createContext, useState, useContext, useEffect } from 'react';
import { EventBus } from '../eventBus';
import axios from 'axios';
import { getExpirationTime, getUser, removeUser, storeUser } from '../utils/tokens';
import { useUserDispatch } from './user';
import { useClientSettingsDispatch } from './client-settings';

const AuthStateContext = createContext(undefined);
const AuthDispatchContext = createContext(undefined);

const loginUrl = '/useraccount/login';
const logoutUrl = () => `useraccount/logout`;
const settingsUrl = clientId => `accountsettings/v1/settings?clientId=${clientId}`;
const AuthProvider = ({ children }) => {
	const [inFrame, setInFrame] = useState(false);
	const [isAuthenticated, setIsAuthenticated] = useState(false);
	const [isAuthenticatedWithProvider] = useState(false);
	const userDispatch = useUserDispatch();
	const clientSettingsDispatch = useClientSettingsDispatch();

	useEffect(() => {
		EventBus.$on('authChange', val => {
			setIsAuthenticated(val);
		});
		return () => {
			EventBus.$off('authChange');
		};
	}, []);

	const checkAuthentication = () => {
		const user = getUser();

		try {
			const expirationTime = getExpirationTime() * 1000;
			const now = +new Date();
			setIsAuthenticated(expirationTime > now);
		} catch (e) {
			setIsAuthenticated(false);
		}
		userDispatch(user);
	};

	useEffect(checkAuthentication, [userDispatch, clientSettingsDispatch]);

	const logout = () =>
		axios({
			method: 'GET',
			url: logoutUrl(),
			skipAuthRefresh: true,
		})
			.then(removeUser)
			.then(checkAuthentication);

	const frameAuth = val => {
		setInFrame(val);
	};

	const login = values => {
		const { username, password, company } = values;
		const encodedBase64Token = btoa(username + ':' + password);
		const authorization = 'Basic ' + encodedBase64Token;

		const promise = axios({
			method: 'POST',
			url: loginUrl,
			headers: {
				Authorization: authorization,
				ETWClientId: company,
			},
			skipAuthRefresh: true,
		});

		return postLoginSetup(promise);
	};

	const postLoginSetup = promise => {
		return promise.then(storeUser).then(checkAuthentication);
	};

	const isIframe = () => {
		return window.location !== window.parent.location;
	};

	function retrieveTokens(authKey) {
		return axios.get('useraccount/login-with-auth-code?code=' + authKey).then(
			response => {
				storeUser(response);
				EventBus.$emit('authChange', true);
				return response;
			},
			error => {
				console.log(error);
			},
		);
	}

	return (
		<AuthStateContext.Provider value={{ isAuthenticated, isAuthenticatedWithProvider }}>
			<AuthDispatchContext.Provider
				value={{
					// setPasswordEmail,
					// forgotPassword,
					frameAuth,
					isIframe,
					login,
					logout,
					// setProviderAuthenticated,
					// setPassword,
					postLoginSetup,
				}}>
				{children}
			</AuthDispatchContext.Provider>
		</AuthStateContext.Provider>
	);
};

const getClientSettings = id => {
	return axios({
		method: 'GET',
		url: settingsUrl(id),
	}).then(
		response => {
			return response.data;
		},
		error => {
			console.log(error);
		},
	);
};

function getTokens(authKey) {
	return axios.get('useraccount/login-with-auth-code?code=' + authKey).then(
		response => {
			storeUser(response);
			EventBus.$emit('authChange', true);
			return response;
		},
		error => {
			console.log(error);
		},
	);
}

const useAuthState = () => {
	const context = useContext(AuthStateContext);
	if (context === undefined) {
		throw new Error('useAuthState must be used within an AuthProvider');
	}
	return context;
};

const useAuthDispatch = () => {
	const context = useContext(AuthDispatchContext);
	if (context === undefined) {
		throw new Error('useAuthDispatch must be used within an AuthProvider');
	}
	return context;
};

export { AuthProvider, useAuthState, useAuthDispatch, getClientSettings, getTokens };
